/**
 * Persists key lists in localStorage so a flaky API / refresh does not leave the UI empty.
 * Logout uses localStorage.clear() — these entries are cleared with the session.
 */

const NS = 'cc.data.v2';
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const storageKey = (userId, segment) => `${NS}.${segment}.${String(userId)}`;

export function getCurrentUserIdFromStorage() {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const u = JSON.parse(raw);
        return u?.id || u?._id || null;
    } catch {
        return null;
    }
}

function readEntry(fullKey, maxAgeMs) {
    try {
        const raw = localStorage.getItem(fullKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.t !== 'number') return null;
        if (Date.now() - parsed.t > maxAgeMs) {
            localStorage.removeItem(fullKey);
            return null;
        }
        return parsed.v;
    } catch {
        try {
            localStorage.removeItem(fullKey);
        } catch {
            /* ignore */
        }
        return null;
    }
}

function writeEntry(fullKey, value) {
    const payload = JSON.stringify({ t: Date.now(), v: value });
    try {
        localStorage.setItem(fullKey, payload);
    } catch (e) {
        if (String(e?.name) === 'QuotaExceededError' || e?.code === 22) {
            try {
                localStorage.removeItem(fullKey);
                localStorage.setItem(fullKey, payload);
            } catch {
                console.warn('[clientDataCache] storage quota exceeded for', fullKey);
            }
        }
    }
}

export const clientDataCache = {
    readJobs(userId, maxAgeMs = DEFAULT_MAX_AGE_MS) {
        if (!userId) return null;
        const v = readEntry(storageKey(userId, 'jobs'), maxAgeMs);
        return Array.isArray(v) ? v : null;
    },

    writeJobs(userId, jobs) {
        if (!userId || !Array.isArray(jobs) || jobs.length === 0) return;
        const capped = jobs.slice(0, 60);
        writeEntry(storageKey(userId, 'jobs'), capped);
    },

    readSavedJobs(userId, maxAgeMs = DEFAULT_MAX_AGE_MS) {
        if (!userId) return null;
        const v = readEntry(storageKey(userId, 'savedJobs'), maxAgeMs);
        return Array.isArray(v) ? v : null;
    },

    writeSavedJobs(userId, savedJobs) {
        if (!userId || !Array.isArray(savedJobs) || savedJobs.length === 0) return;
        const capped = savedJobs.slice(0, 80);
        writeEntry(storageKey(userId, 'savedJobs'), capped);
    },

    readReferralStrategy(userId, organizationId, maxAgeMs = 30 * 24 * 60 * 60 * 1000) {
        if (!userId || !organizationId) return null;
        return readEntry(storageKey(userId, `referralStrategy.${String(organizationId)}`), maxAgeMs);
    },

    writeReferralStrategy(userId, organizationId, payload) {
        if (!userId || !organizationId || !payload) return;
        writeEntry(storageKey(userId, `referralStrategy.${String(organizationId)}`), payload);
    },

    readTaskTimeline(userId, maxAgeMs = DEFAULT_MAX_AGE_MS) {
        if (!userId) return null;
        const v = readEntry(storageKey(userId, 'taskTimeline'), maxAgeMs);
        return Array.isArray(v) ? v : null;
    },

    writeTaskTimeline(userId, timeline) {
        if (!userId || !Array.isArray(timeline)) return;
        const capped = timeline.slice(0, 500);
        writeEntry(storageKey(userId, 'taskTimeline'), capped);
    },

    readStrategyAdvice(userId, key, maxAgeMs = 30 * 24 * 60 * 60 * 1000) {
        if (!userId || !key) return null;
        return readEntry(storageKey(userId, `strategyAdvice.${String(key)}`), maxAgeMs);
    },

    writeStrategyAdvice(userId, key, advice) {
        if (!userId || !key || !advice) return;
        writeEntry(storageKey(userId, `strategyAdvice.${String(key)}`), advice);
    }
};
